import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ProgramsV2.module.css";
import Header from '../.shared/header/header';
import Footer from '../.shared/footer/footer';
import Pricing from "../../data/Pricing";
import semaDropsImg from "../../assets/semaglutide-suspension.webp";
import semaInjectableImg from "../../assets/semaglutide-injectable.webp";
import tirzDropsImg from "../../assets/tirzepatide-suspension.webp";

/* ===== Inline SVG icons ===== */
const DropsIcon = () => (
    <svg width="9" height="12" viewBox="0 0 9 12" fill="currentColor">
        <path d="M4.5 0C2 3 0 5.5 0 8a4.5 4.5 0 0 0 9 0c0-2.5-2-5-4.5-8z"/>
    </svg>
);

const InjectableIcon = () => (
    <svg width="11" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 4l-3-3-2 2-9 9-1.5 4.5L8 15l9-9 2-2zM5 18l-2 4 4-2-2-2z"/>
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <polyline points="5 12 10 17 19 7"/>
    </svg>
);

const GiftIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 6h-3.17l.42-.59C18 4.27 17.65 3 16.41 3 15.83 3 15.27 3.26 14.9 3.7L12 7l-2.9-3.3C8.73 3.26 8.17 3 7.59 3 6.35 3 6 4.27 6.75 5.41L7.17 6H4a2 2 0 0 0-2 2v3h2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9h2V8a2 2 0 0 0-2-2z"/>
    </svg>
);

const ShieldIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 1l10 4v6c0 5.5-3.8 10.7-10 12-6.2-1.3-10-6.5-10-12V5l10-4z"/>
    </svg>
);

const TruckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 6h13v8h2l3-4h-4V7h-1V5H3v1zm14 11a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM7 17a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
    </svg>
);

const ChatIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M21 6h-2v9H6v2a1 1 0 0 0 1 1h11l4 4V7a1 1 0 0 0-1-1zm-4 7V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v14l4-4h10a1 1 0 0 0 1-1z"/>
    </svg>
);

const ClockIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 11h-5v-2h3V6h2v7z"/>
    </svg>
);

/* ===== Price formatter ===== */
const fmt = (val) => {
    const n = parseFloat(val);
    return n % 1 === 0 ? n.toString() : n.toFixed(2);
};

/* ===== ProgramCard ===== */
function ProgramCard({ tint, formTag, freqTag, img, name, form, plan, features, giftCard, featured, ribbonText, ctaText, onSelect }) {
    const isQuarterly = plan.subscription_length === '3 Months';

    return (
        <article className={`${styles.card} ${featured ? styles.cardFeatured : styles.cardOffset}`}>
            {featured && ribbonText && (
                <div className={styles.ribbon}>
                    <GiftIcon />
                    {ribbonText}
                </div>
            )}
            <div className={`${styles.bodyWrapper} ${featured ? styles.bodyFeatured : ''}`}>
                <div className={`${styles.cardTop} ${styles[tint]}`}>
                    <div className={styles.tagRow}>
                        <span className={styles.pillTag}>
                            {formTag === 'INJECTABLE' ? <InjectableIcon /> : <DropsIcon />}
                            {formTag}
                        </span>
                        <span className={`${styles.pillTag} ${styles.pillTagDark}`}>{freqTag}</span>
                    </div>
                    <div className={styles.productImg}>
                        <img src={img} alt={name} />
                    </div>
                </div>

                <div className={styles.cardInfo}>
                    <div>
                        <div className={styles.medName}>{name}</div>
                        <div className={styles.medForm}>{form}</div>
                        <div className={styles.priceRow}>
                            {isQuarterly ? (
                                <>
                                    <span className={styles.priceTotal}>${fmt(plan.total)}</span>
                                    <span className={styles.per}>&nbsp;total&nbsp;—&nbsp;</span>
                                    <span className={styles.priceMo}>${fmt(plan.monthly_amount)}/mo</span>
                                </>
                            ) : (
                                <>
                                    <span className={styles.price}>${fmt(plan.monthly_amount)}</span>
                                    <span className={styles.per}>/month</span>
                                </>
                            )}
                        </div>
                    </div>

                    <ul className={styles.features}>
                        {features.map((f, i) => (
                            <li key={i} className={styles.feat}>
                                <span className={styles.tick}><CheckIcon /></span>
                                {f}
                            </li>
                        ))}
                    </ul>

                    {giftCard && (
                        <div className={styles.giftCard}>
                            <span className={styles.giftIco}><GiftIcon /></span>
                            <div>
                                <div className={styles.giftTitle}>{giftCard.title}</div>
                                <div className={styles.giftSub}>{giftCard.sub}</div>
                            </div>
                        </div>
                    )}
                </div>

                <button
                    className={`${styles.cta} ${featured ? styles.ctaCoral : ''}`}
                    onClick={onSelect}
                >
                    {ctaText}
                </button>
            </div>
        </article>
    );
}

/* ===== Main page ===== */
export default function ProgramsV2() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState('all');

    const pricingInstance = Pricing();
    const sema = pricingInstance.getPricingOptionsByProductName('semaglutide');
    const tirz = pricingInstance.getPricingOptionsByProductName('tirzepatide');

    const showMonthly = filter !== '3month';
    const showQuarterly = filter !== 'monthly';

    const handleSelect = (planId) => {
        localStorage.setItem('plan_id', planId);
        navigate('/checkout/payment');
    };

    /* Savings calculations for featured Sema Injectable cards */
    const semaMonthlyAmt = parseFloat(sema.injectable.monthly.monthly_amount);
    const thirdMonthFreeEffective = Math.round((semaMonthlyAmt * 2) / 3);

    const sema3moTotal = parseFloat(sema.injectable.threeMonths.total);
    const sema3moSavingsAmt = Math.round(semaMonthlyAmt * 3 - sema3moTotal);
    const sema3moSavingsPct = Math.round((sema3moSavingsAmt / (semaMonthlyAmt * 3)) * 100);

    return (
        <>
            <Header />
            <div className={styles.page}>

                {/* ===== Banner ===== */}
                <div className={styles.contentWrap}>
                    <section className={styles.banner}>
                        <div className={styles.bannerCopy}>
                            <div className={styles.bannerHeadline}>
                                <span className={styles.accentText}>Clinician-guided</span> weight management, tailored to your needs.
                            </div>
                            <div className={styles.bannerSubtitle}>
                                Two medications, two dosage forms, and a plan that fits your pace.
                            </div>
                        </div>

                        <div className={styles.filterToggle} role="tablist">
                            <button
                                className={`${styles.filterSeg} ${filter === 'all' ? styles.filterSegActive : ''}`}
                                onClick={() => setFilter('all')}
                                role="tab"
                                aria-selected={filter === 'all'}
                            >
                                All plans
                            </button>
                            <button
                                className={`${styles.filterSeg} ${filter === 'monthly' ? styles.filterSegActive : ''}`}
                                onClick={() => setFilter('monthly')}
                                role="tab"
                                aria-selected={filter === 'monthly'}
                            >
                                Month-to-month
                            </button>
                            <button
                                className={`${styles.filterSeg} ${filter === '3month' ? styles.filterSegActive : ''}`}
                                onClick={() => setFilter('3month')}
                                role="tab"
                                aria-selected={filter === '3month'}
                            >
                                3-month
                                <span className={styles.saveBadge}>Save</span>
                            </button>
                        </div>
                    </section>

                    {/* ===== Monthly section ===== */}
                    {showMonthly && (
                        <>
                            <div className={styles.sectionLabelRow}>
                                <span className={styles.sectionName}>Month-to-month</span>
                                <span className={styles.sectionBadge}>FLEXIBLE — CANCEL ANYTIME</span>
                            </div>

                            <div className={styles.cardRow}>
                                <ProgramCard
                                    tint="tintMint"
                                    formTag="ORAL DROPS"
                                    freqTag="DAILY"
                                    img={semaDropsImg}
                                    name="Semaglutide"
                                    form="Oral drops · Daily under-tongue dose"
                                    plan={sema.suspension.monthly}
                                    features={[
                                        'No needles — simple sublingual dosing',
                                        'Clinician evaluation & ongoing care',
                                        'Free shipping. Cancel anytime',
                                    ]}
                                    ctaText="Start Semaglutide Drops"
                                    onSelect={() => handleSelect(sema.suspension.monthly.id)}
                                />

                                <ProgramCard
                                    tint="tintPink"
                                    formTag="INJECTABLE"
                                    freqTag="WEEKLY"
                                    img={semaInjectableImg}
                                    name="Semaglutide"
                                    form="Injectable · Once-weekly"
                                    plan={sema.injectable.monthly}
                                    features={[
                                        'Once-weekly self-administered injection',
                                        'Refrigerated delivery included',
                                        'Clinician evaluation & ongoing care',
                                    ]}
                                    giftCard={{
                                        title: 'Your 3rd month is FREE',
                                        sub: `Save $${fmt(sema.injectable.monthly.monthly_amount)} over 3 months — just $${thirdMonthFreeEffective}/mo effective`,
                                    }}
                                    featured
                                    ribbonText="Your 3rd month is FREE"
                                    ctaText="Claim 3rd month free"
                                    onSelect={() => handleSelect(sema.injectable.monthly.id)}
                                />

                                <ProgramCard
                                    tint="tintBlue"
                                    formTag="ORAL DROPS"
                                    freqTag="DAILY"
                                    img={tirzDropsImg}
                                    name="Tirzepatide"
                                    form="Oral drops · Daily under-tongue dose"
                                    plan={tirz.suspension.monthly}
                                    features={[
                                        'Dual GIP/GLP-1 agonist — no needles',
                                        'Clinician evaluation & ongoing care',
                                        'Free shipping. Cancel anytime',
                                    ]}
                                    ctaText="Start Tirzepatide Drops"
                                    onSelect={() => handleSelect(tirz.suspension.monthly.id)}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* ===== 3-month section ===== */}
                {showQuarterly && (
                    <div className={styles.bandSection}>
                        <div className={styles.contentWrap}>
                            <div className={`${styles.sectionLabelRow} ${styles.sectionLabelRowNoShadow}`}>
                                <span className={`${styles.sectionBadge} ${styles.badgeCoral}`}>3 Month Program</span>
                                <span className={`${styles.sectionBadge} ${styles.badgeCoralLight}`}>
                                    BEST VALUE — SAVE UP TO {sema3moSavingsPct}%
                                </span>
                            </div>

                            <div className={styles.cardRow}>
                                <ProgramCard
                                    tint="tintMint"
                                    formTag="ORAL DROPS"
                                    freqTag="3 MONTHS"
                                    img={semaDropsImg}
                                    name="Semaglutide"
                                    form="Oral drops · 3-month program"
                                    plan={sema.suspension.threeMonths}
                                    features={[
                                        '3-month supply shipped at once',
                                        'Locked-in clinician check-ins',
                                        `Save $${Math.round(semaMonthlyAmt * 3 - parseFloat(sema.suspension.threeMonths.total))} vs. month-to-month`,
                                    ]}
                                    ctaText="Choose 3-month plan"
                                    onSelect={() => handleSelect(sema.suspension.threeMonths.id)}
                                />

                                <ProgramCard
                                    tint="tintPink"
                                    formTag="INJECTABLE"
                                    freqTag="3 MONTHS"
                                    img={semaInjectableImg}
                                    name="Semaglutide"
                                    form="Injectable · Once-weekly"
                                    plan={sema.injectable.threeMonths}
                                    features={[
                                        '3 months of weekly doses prepaid',
                                        'Priority clinician access',
                                        'Refrigerated delivery included',
                                    ]}
                                    giftCard={{
                                        title: `Save $${sema3moSavingsAmt} on the 3-month plan`,
                                        sub: `Just $${fmt(sema.injectable.threeMonths.monthly_amount)}/mo — ${sema3moSavingsPct}% less than monthly`,
                                    }}
                                    featured
                                    ribbonText={`BEST VALUE — ${sema3moSavingsPct}% SAVINGS`}
                                    ctaText={`Claim ${sema3moSavingsPct}% savings plan`}
                                    onSelect={() => handleSelect(sema.injectable.threeMonths.id)}
                                />

                                <ProgramCard
                                    tint="tintBlue"
                                    formTag="ORAL DROPS"
                                    freqTag="3 MONTHS"
                                    img={tirzDropsImg}
                                    name="Tirzepatide"
                                    form="Oral drops · 3-month program"
                                    plan={tirz.suspension.threeMonths}
                                    features={[
                                        '3-month supply shipped at once',
                                        'Locked-in clinician check-ins',
                                        `Save $${Math.round(parseFloat(tirz.suspension.monthly.monthly_amount) * 3 - parseFloat(tirz.suspension.threeMonths.total))} vs. month-to-month`,
                                    ]}
                                    ctaText="Choose 3-month plan"
                                    onSelect={() => handleSelect(tirz.suspension.threeMonths.id)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* ===== Feature strip + FAQ + Disclaimer ===== */}
                <div className={styles.contentWrap}>

                    <div className={styles.featureStrip}>
                        <div className={styles.featureCard}>
                            <span className={styles.featureIco}><ShieldIcon /></span>
                            <div>
                                <div className={styles.featureTitle}>Licensed clinicians</div>
                                <div className={styles.featureBody}>Every intake is reviewed by a licensed clinician who decides whether treatment is appropriate.</div>
                            </div>
                        </div>
                        <div className={styles.featureCard}>
                            <span className={styles.featureIco}><TruckIcon /></span>
                            <div>
                                <div className={styles.featureTitle}>Free Shipping</div>
                                <div className={styles.featureBody}>Discreet delivery on every plan. Injectables ship refrigerated with handling instructions included.</div>
                            </div>
                        </div>
                        <div className={styles.featureCard}>
                            <span className={styles.featureIco}><ChatIcon /></span>
                            <div>
                                <div className={styles.featureTitle}>Ongoing support</div>
                                <div className={styles.featureBody}>Message your care team anytime. Dose adjustments are clinician-guided and included in every plan.</div>
                            </div>
                        </div>
                        <div className={styles.featureCard}>
                            <span className={styles.featureIco}><ClockIcon /></span>
                            <div>
                                <div className={styles.featureTitle}>Cancel anytime</div>
                                <div className={styles.featureBody}>No long-term lock-in on monthly plans. 3-month plans can be paused if treatment changes.</div>
                            </div>
                        </div>
                    </div>

                    <section className={styles.faq}>
                        <p className={styles.faqTitle}>Have questions about our programs?</p>
                        <button className={styles.faqButton} onClick={() => navigate('/knowledge-center')}>
                            View our FAQ section
                        </button>
                        <p className={styles.faqDesc}>for detailed answers about GLP-1 medications, dosage, side effects, and more.</p>
                    </section>

                    <div className={styles.disclaimer}>
                        <p>All patients undergo a thorough medical evaluation to ensure GLP-1 therapy is safe and appropriate. If you are determined ineligible for a prescription after clinical assessment, you will receive a full refund.</p>
                        <p>†Ozempic® and Wegovy® are registered trademarks of Novo Nordisk A/S. Mounjaro® and Zepbound® are registered trademarks of Eli Lilly and Company. These trademark holders are not affiliated with and do not endorse TB2 Health.</p>
                        <p>Our telehealth weight loss programs are designed to help you achieve your weight loss goals safely and effectively. Programs are subscription-based, with either monthly or quarterly recurring payments. They include a consultation with a Clinician licensed in your state assisted by a Patient Care Coordinator, whom you can contact with any questions or concerns at any time. Depending on the regulations in your state, this may include a call or video visit. Based on information gathered during your consultation and your choices, the Clinician may prescribe a GLP-1 agonist that is right for you. Monthly check-ins and follow-ups are included, along with free shipping to your door. Cancel at any time.</p>
                    </div>

                </div>
            </div>
            <Footer />
        </>
    );
}
